import React, { useEffect, useState, useCallback } from 'react'; // <--- 1. Import useCallback
import axios from 'axios';

const SliderManager = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({ imageUrl: '', displayOrder: '' });
  const [isEditing, setIsEditing] = useState(null); 

  // API Configuration
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const API_BASE_URL = isLocal ? "http://localhost:5279" : "https://dmsserver.nsfdcdigital.in";

  // --- 2. WRAP FUNCTION IN useCallback ---
  // This ensures the function instance stays the same unless API_BASE_URL changes
  const fetchSliders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/slider/all`);
      setSliders(res.data);
    } catch (error) {
      alert("Failed to load sliders");
    }
  }, [API_BASE_URL]); // Dependency: Only recreate if URL changes

  // --- 3. ADD TO DEPENDENCY ARRAY ---
  useEffect(() => {
    fetchSliders();
  }, [fetchSliders]); // Now safe to include

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageUrl) return alert("Please enter an Image URL");

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/api/slider/${isEditing}`, {
          ImageUrl: formData.imageUrl,
          DisplayOrder: parseInt(formData.displayOrder) || 0
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/slider/add`, {
          ImageUrl: formData.imageUrl,
          DisplayOrder: parseInt(formData.displayOrder) || 0
        });
      }
      
      resetForm();
      fetchSliders();
    } catch (error) {
      alert("Operation failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slide) => {
    setIsEditing(slide.id);
    setFormData({ 
      imageUrl: slide.imageUrl, 
      displayOrder: slide.displayOrder 
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this slide?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/slider/${id}`);
      fetchSliders();
    } catch (error) {
      alert("Delete failed");
    }
  };

  const resetForm = () => {
    setIsEditing(null);
    setFormData({ imageUrl: '', displayOrder: '' });
  };

  return (
    <div className="container mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="text-black fw-bold">
          <i className="bi bi-images me-2"></i>App Slider Manager
        </h4>
      </div>

      {/* --- ADD / EDIT FORM --- */}
      <div className="card shadow-sm p-4 mb-5 border-primary">
        <h5 className="mb-3">{isEditing ? "Edit Slide" : "Add New Slide URL"}</h5>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label">Image URL</label>
              <input 
                type="url" 
                className="form-control" 
                placeholder="https://example.com/image.jpg"
                value={formData.imageUrl}
                onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Order</label>
              <input 
                type="number" 
                className="form-control" 
                placeholder="1"
                value={formData.displayOrder}
                onChange={(e) => setFormData({...formData, displayOrder: e.target.value})}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <div className="d-grid gap-2 w-100">
                <button type="submit" className="btn btn-success" disabled={loading}>
                  {loading ? "Saving..." : (isEditing ? "Update" : "Add Slide")}
                </button>
                {isEditing && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* --- SLIDER GRID --- */}
      <div className="row g-4">
        {sliders.length === 0 && (
          <div className="text-center text-muted py-5">No slides found. Add one above!</div>
        )}
        
        {sliders.map((slide) => (
          <div key={slide.id} className="col-md-4 col-lg-3">
            <div className="card h-100 shadow-sm hover-shadow">
              <div className="position-relative" style={{ height: '160px', overflow: 'hidden', backgroundColor: '#e9ecef' }}>
                <img 
                  src={slide.imageUrl} 
                  alt="Slide" 
                  className="card-img-top w-100 h-100"
                  style={{ objectFit: 'cover' }}
                  onError={(e) => e.target.src = "https://via.placeholder.com/400x200?text=Invalid+Image+URL"} 
                />
                <span className="position-absolute top-0 end-0 badge bg-dark m-2 opacity-75">
                  #{slide.displayOrder}
                </span>
              </div>
              
              <div className="card-body p-3">
                <p className="card-text text-truncate small text-muted mb-3" title={slide.imageUrl}>
                  <i className="bi bi-link-45deg me-1"></i>
                  {slide.imageUrl}
                </p>
                
                <div className="d-flex justify-content-between">
                  <button 
                    className="btn btn-sm btn-outline-primary" 
                    onClick={() => handleEdit(slide)}
                  >
                    <i className="bi bi-pencil-square me-1"></i> Edit
                  </button>
                  <button 
                    className="btn btn-sm btn-outline-danger" 
                    onClick={() => handleDelete(slide.id)}
                  >
                    <i className="bi bi-trash me-1"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SliderManager;